// ไฟล์: frontend/src/pages/admin/Teachers.js
import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import api from '../../services/api';
import Swal from 'sweetalert2';
import { DEFAULT_AVATAR } from '../../assets/default_avatar';

function AdminTeachers() {
  const [teachers, setTeachers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    username: '',
    password: '',
    first_name: '',
    last_name: '',
    email: '',
    profile_pic: null
  });
  const [formMode, setFormMode] = useState('add'); // 'add' หรือ 'edit'
  const [previewImage, setPreviewImage] = useState(null);
  
  useEffect(() => {
    fetchTeachers();
  }, []);
  
  const fetchTeachers = async () => {
    try {
      setIsLoading(true);
      const response = await api.user.getAllTeachers();
      setTeachers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching teachers:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลครูผู้สอนได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleShowAddModal = () => {
    setFormData({
      id: '',
      username: '',
      password: '',
      first_name: '',
      last_name: '',
      email: '',
      profile_pic: null
    });
    setPreviewImage(null);
    setFormMode('add');
    setShowModal(true);
  };
  
  const handleShowEditModal = (teacher) => {
    setFormData({
      id: teacher.id,
      username: teacher.username,
      password: '',
      first_name: teacher.first_name,
      last_name: teacher.last_name,
      email: teacher.email || '',
      profile_pic: null
    });
    setPreviewImage(
      teacher.profile_pic 
        ? `https://cms2568.backend.kruwirat.com/uploads/${teacher.profile_pic}` 
        : null
    );
    setFormMode('edit');
    setShowModal(true);
  };
  
  const handleCloseModal = () => {
    setShowModal(false);
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        profile_pic: file
      });
      
      // สร้าง URL สำหรับแสดงตัวอย่างรูปภาพ
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // ตรวจสอบข้อมูลที่จำเป็น
    if (!formData.username || !formData.first_name || !formData.last_name) {
      Swal.fire({
        icon: 'warning',
        title: 'กรุณากรอกข้อมูลให้ครบถ้วน',
        text: 'กรุณากรอกชื่อผู้ใช้ ชื่อ และนามสกุล',
        confirmButtonColor: '#ff6b01'
      });
      return;
    }
    
    // ตรวจสอบรหัสผ่านกรณีเพิ่มใหม่
    if (formMode === 'add' && !formData.password) {
      Swal.fire({
        icon: 'warning',
        title: 'กรุณากรอกรหัสผ่าน',
        text: 'กรุณากรอกรหัสผ่านสำหรับผู้ใช้ใหม่',
        confirmButtonColor: '#ff6b01'
      });
      return;
    }
    
    try {
      setIsLoading(true);
      
      // สร้าง FormData สำหรับส่งข้อมูลพร้อมรูปภาพ
      const data = new FormData();
      data.append('username', formData.username);
      data.append('first_name', formData.first_name);
      data.append('last_name', formData.last_name);
      data.append('email', formData.email);
      data.append('role', 'teacher');
      
      if (formMode === 'add' || (formMode === 'edit' && formData.password)) {
        data.append('password', formData.password);
      }
      
      if (formData.profile_pic) {
        data.append('profile_pic', formData.profile_pic);
      }
      
      let response;
      if (formMode === 'add') {
        // เพิ่มครูใหม่
        response = await api.user.createUser(data);
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: 'เพิ่มครูผู้สอนเรียบร้อยแล้ว',
          confirmButtonColor: '#ff6b01'
        });
      } else {
        // แก้ไขข้อมูลครู
        response = await api.user.updateUser(formData.id, data);
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: 'แก้ไขข้อมูลครูผู้สอนเรียบร้อยแล้ว',
          confirmButtonColor: '#ff6b01'
        });
      }
      
      // ปิด Modal และโหลดข้อมูลใหม่
      setShowModal(false);
      fetchTeachers();
    } catch (error) {
      console.error('Error saving teacher:', error);
      let errorMsg = 'ไม่สามารถบันทึกข้อมูลครูผู้สอนได้';
      
      if (error.response && error.response.data && error.response.data.message) {
        errorMsg = error.response.data.message;
      }
      
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: errorMsg,
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDelete = (id) => {
    Swal.fire({
      title: 'ยืนยันการลบ',
      text: 'คุณแน่ใจหรือไม่ว่าต้องการลบข้อมูลครูผู้สอนนี้?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ff6b01',
      cancelButtonColor: '#d33',
      confirmButtonText: 'ใช่, ลบข้อมูล',
      cancelButtonText: 'ยกเลิก'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.user.deleteUser(id);
          Swal.fire({
            icon: 'success',
            title: 'ลบข้อมูลสำเร็จ',
            text: 'ลบข้อมูลครูผู้สอนเรียบร้อยแล้ว',
            confirmButtonColor: '#ff6b01'
          });
          fetchTeachers();
        } catch (error) {
          console.error('Error deleting teacher:', error);
          Swal.fire({
            icon: 'error',
            title: 'เกิดข้อผิดพลาด',
            text: 'ไม่สามารถลบข้อมูลครูผู้สอนได้',
            confirmButtonColor: '#ff6b01'
          });
        }
      }
    });
  };
  
  const handleResetPassword = (id, username) => {
    Swal.fire({
      title: 'รีเซ็ตรหัสผ่าน',
      text: `คุณต้องการรีเซ็ตรหัสผ่านของ ${username} ใช่หรือไม่?`,
      input: 'password',
      inputLabel: 'รหัสผ่านใหม่',
      inputPlaceholder: 'กรอกรหัสผ่านใหม่',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#ff6b01',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
      inputValidator: (value) => {
        if (!value) {
          return 'กรุณากรอกรหัสผ่านใหม่';
        }
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.user.changePassword(id, {
            current_password: '',
            new_password: result.value
          });
          
          Swal.fire({
            icon: 'success',
            title: 'รีเซ็ตรหัสผ่านสำเร็จ',
            text: 'รหัสผ่านถูกเปลี่ยนเรียบร้อยแล้ว',
            confirmButtonColor: '#ff6b01'
          });
        } catch (error) {
          console.error('Error resetting password:', error);
          Swal.fire({
            icon: 'error',
            title: 'เกิดข้อผิดพลาด',
            text: 'ไม่สามารถรีเซ็ตรหัสผ่านได้',
            confirmButtonColor: '#ff6b01'
          });
        }
      }
    });
  };
  
  return (
    <div className="wrapper">
      <Header />
      <Sidebar />
      
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">จัดการข้อมูลครูผู้สอน</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <a href="/admin/dashboard">หน้าหลัก</a>
                  </li>
                  <li className="breadcrumb-item active">ครูผู้สอน</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        
        <section className="content">
          <div className="container-fluid">
            <div className="card card-orange">
              <div className="card-header">
                <h3 className="card-title">รายชื่อครูผู้สอนทั้งหมด</h3>
                <div className="card-tools">
                  <button
                    type="button"
                    className="btn btn-tool"
                    onClick={handleShowAddModal}
                  >
                    <i className="fas fa-plus"></i> เพิ่มครูผู้สอน
                  </button>
                </div>
              </div>
              <div className="card-body">
                {isLoading ? (
                  <div className="loading-spinner">
                    <i className="fas fa-spinner fa-spin"></i>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-bordered table-striped">
                      <thead>
                        <tr>
                          <th style={{ width: '80px' }}>รูปภาพ</th>
                          <th style={{ width: '150px' }}>ชื่อผู้ใช้</th>
                          <th>ชื่อ-นามสกุล</th>
                          <th style={{ width: '180px' }}>อีเมล</th>
                          <th style={{ width: '200px' }}>ตัวเลือก</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teachers.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="text-center">
                              ไม่พบข้อมูลครูผู้สอน
                            </td>
                          </tr>
                        ) : (
                          teachers.map((teacher) => (
                            <tr key={teacher.id}>
                              <td>
                                <img
                                  src={`https://cms2568.backend.kruwirat.com/uploads/${teacher.profile_pic}`}
                                  alt={`${teacher.first_name} ${teacher.last_name}`}
                                  className="student-profile-pic-sm"
                                  onError={(e) => { e.target.onerror = null; e.target.src = DEFAULT_AVATAR; }}
                                  />
                              </td>
                              <td>{teacher.username}</td>
                              <td>
                                {teacher.first_name} {teacher.last_name}
                              </td>
                              <td>{teacher.email || '-'}</td>
                              <td>
                                <button
                                  className="btn btn-info btn-sm mr-1"
                                  onClick={() => handleShowEditModal(teacher)}
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  className="btn btn-warning btn-sm mr-1"
                                  onClick={() => handleResetPassword(teacher.id, teacher.username)}
                                >
                                  <i className="fas fa-key"></i>
                                </button>
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => handleDelete(teacher.id)}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
      
      {/* Modal เพิ่ม/แก้ไขข้อมูลครูผู้สอน */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block', paddingRight: '15px' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-orange">
                <h5 className="modal-title">
                  {formMode === 'add' ? 'เพิ่มครูผู้สอนใหม่' : 'แก้ไขข้อมูลครูผู้สอน'}
                </h5>
                <button type="button" className="close" onClick={handleCloseModal}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-4 text-center">
                      <div className="form-group">
                        <label>รูปโปรไฟล์</label>
                        <div className="text-center mb-2">
                          <img
                            src={previewImage || 'https://via.placeholder.com/150'}
                            alt="Preview"
                            className="student-profile-pic"
                            style={{ width: '150px', height: '150px' }}
                          />
                        </div>
                        <div className="custom-file">
                          <input
                            type="file"
                            className="custom-file-input"
                            id="profile_pic"
                            name="profile_pic"
                            onChange={handleFileChange}
                            accept="image/*"
                          />
                          <label className="custom-file-label" htmlFor="profile_pic">
                            เลือกรูปภาพ
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-8">
                      <div className="form-group">
                        <label>ชื่อผู้ใช้ <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          name="username"
                          value={formData.username}
                          onChange={handleInputChange}
                          required
                          disabled={formMode === 'edit'} // ไม่อนุญาตให้แก้ไขชื่อผู้ใช้
                        />
                        {formMode === 'edit' && (
                          <small className="form-text text-muted">
                            ไม่สามารถแก้ไขชื่อผู้ใช้ได้
                          </small>
                        )}
                      </div>
                      
                      {formMode === 'add' && (
                        <div className="form-group">
                          <label>รหัสผ่าน <span className="text-danger">*</span></label>
                          <input
                            type="password"
                            className="form-control"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      )}
                      
                      {formMode === 'edit' && (
                        <div className="form-group">
                          <label>รหัสผ่านใหม่</label>
                          <input
                            type="password"
                            className="form-control"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            placeholder="เว้นว่างไว้หากไม่ต้องการเปลี่ยนรหัสผ่าน"
                          />
                          <small className="form-text text-muted">
                            เว้นว่างไว้หากไม่ต้องการเปลี่ยนรหัสผ่าน
                          </small>
                        </div>
                      )}
                      
                      <div className="form-group">
                        <label>ชื่อ <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>นามสกุล <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>อีเมล</label>
                        <input
                          type="email"
                          className="form-control"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                    ยกเลิก
                  </button>
                  <button type="submit" className="btn btn-orange" disabled={isLoading}>
                    {isLoading ? (
                      <><i className="fas fa-spinner fa-spin"></i> กำลังบันทึก...</>
                    ) : (
                      <>บันทึก</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
          
          {/* พื้นหลังทึบ */}
          <div className="modal- fade show"></div>
        </div>
      )}
      
      <Footer />
    </div>
  );
}

export default AdminTeachers;